// PII scrubbing for PostHog. Adapts the shared regex helpers in
// `./scrub-helpers.ts` to PostHog's `before_send` (CaptureResult-shaped) and
// `sanitize_properties` shapes.
//
// CLAUDE.md: PostHog must never receive raw family data. Extend the shared
// helpers in scrub-helpers.ts when adding new sensitive fields — both the
// property-level scrubber here and the exception-message scrubber in
// `./server.ts` stay in sync.
import type { CaptureResult } from 'posthog-js'
import {
  redactSensitive,
  redactString,
  scrubHeaders,
} from './scrub-helpers'

type Properties = Record<string, unknown>

// Property keys whose top-level value is itself a header bag and should be
// header-scrubbed (case-insensitive lookup).
const HEADER_KEYS = new Set(['$request_headers', 'headers', 'request_headers'])

// Property keys whose value is a URL string and should run through redactString.
const URL_KEYS = new Set([
  '$current_url',
  '$pathname',
  '$referrer',
  '$initial_current_url',
  '$initial_referrer',
  '$exception_source',
  'url',
])

function scrubProperties(properties: Properties): Properties {
  const out: Properties = {}
  for (const [key, value] of Object.entries(properties)) {
    if (HEADER_KEYS.has(key.toLowerCase()) && value && typeof value === 'object' && !Array.isArray(value)) {
      out[key] = scrubHeaders(value as Record<string, string | string[] | undefined>)
      continue
    }
    if (URL_KEYS.has(key) && typeof value === 'string') {
      out[key] = redactString(value)
      continue
    }
    out[key] = redactSensitive(value)
  }
  return out
}

// Wired into posthog-js init as `before_send`. Receives a CaptureResult
// (or null when an earlier hook dropped the event); returns a scrubbed copy
// or null to drop.
export function beforeSend(payload: CaptureResult | null): CaptureResult | null {
  if (!payload) return payload
  const next: CaptureResult = { ...payload }
  if (next.properties) {
    next.properties = scrubProperties(next.properties as Properties) as CaptureResult['properties']
  }
  return next
}

// Wired into posthog-js init as `sanitize_properties`. Runs on every event's
// properties bag before send; we apply the same property-level scrub.
export function sanitizeProperties(properties: Properties, _event: string): Properties {
  return scrubProperties(properties)
}

export const __testing = { scrubProperties }
